import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import { DRAWER_WIDTH, NARROW_DRAWER_WIDTH } from 'components/common/nav-drawer';
import { NotificationBell } from 'components/common/notification-bell';
import { useOutreachActivityVisible } from 'hooks/use-outreach-activity-visible';
import { useAuthContext } from 'providers/auth';
import { usePageContext } from 'providers/page';
import { FC, useLayoutEffect, useRef } from 'react';
import { useMatch } from 'react-router';
import routes from 'routes';
import { HEADER_ROW_HEIGHT } from 'utils/layout';

interface AppBarProps {
  handleDrawerToggle: () => void;
  handleLogout: () => void;
  handleSearchToggle: () => void;
}

export const AppBar: FC<AppBarProps> = ({ handleDrawerToggle, handleSearchToggle }) => {
  const authContext = useAuthContext();
  const { sidebarCollapsed, leftNavDrawerOpen } = usePageContext();
  // Shared with OutreachActivityProvider so the bell and the feed behind it can't be gated apart.
  const activityVisible = useOutreachActivityVisible();
  // The Companies page renders its own search in its toolbar.
  const onCompaniesPage = !!useMatch(`/${routes.home.companies.path}`);

  const headerRef = useRef<HTMLDivElement>(null);

  // One row on most pages, two where the page has tabs, so the height is measured rather than
  // assumed — `pageHeight()` reads it back.
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    // The observer fires on width changes too; only write when the height actually moved.
    let published = -1;
    const publish = () => {
      if (header.offsetHeight === published) return;
      published = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${published}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return (
    <Stack
      ref={headerRef}
      direction="column"
      flexShrink={0}
      sx={{
        // On the Stack rather than the title row, so the tab row clears the drawer too.
        marginLeft: {
          xs: 0,
          md: sidebarCollapsed ? `${NARROW_DRAWER_WIDTH}px` : `${DRAWER_WIDTH}px`,
        },
        position: 'relative',
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        minHeight={HEADER_ROW_HEIGHT}
      >
        <Stack direction="row" sx={{ alignItems: 'center', minWidth: 0 }}>
          {/* Mobile-only: opens the overlay. Desktop's toggle lives in the drawer. */}
          <IconButton
            color="inherit"
            aria-label="Open navigation"
            aria-expanded={leftNavDrawerOpen}
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Box id="portal-header-title" sx={{ ml: { xs: 1, md: 5 }, minWidth: 0 }} />
        </Stack>

        {/* pr is load-bearing: MUI hangs a Badge half its width outside the icon, so flush
            against the viewport edge a two-digit count overflowed the document and grew a
            scrollbar. */}
        <Stack direction="row" spacing={2} sx={{ pr: 2 }}>
          {activityVisible && <NotificationBell />}
          {authContext.isInternal && !onCompaniesPage && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="search"
              onClick={handleSearchToggle}
            >
              <SearchIcon />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Collapses away on pages without tabs, so the header is a single row there. */}
      <Box id="portal-header-tabs" sx={{ pl: 3, '&:empty': { display: 'none' } }} />
      <Divider />
    </Stack>
  );
};
